import React, { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { generateId } from '../utils/helpers';
import * as api from '../utils/api';
import * as storage from '../utils/storage';
import useAuthStore from '../stores/useAuthStore';
import useAppStore from '../stores/useAppStore';
import { useTasks } from '../hooks/useTasks';
import { useProjects } from '../hooks/useProjects';
import { useAreas } from '../hooks/useAreas';
import { useTags } from '../hooks/useTags';

// Provider — drives the post-sign-in cloud sync. All data lives in React Query.
export function AppProvider({ children }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const wasAuthRef = useRef(isAuthenticated);
  const qc = useQueryClient();

  useEffect(() => {
    if (isAuthenticated && !wasAuthRef.current) {
      // Just transitioned from logged-out → logged-in. Push any local-only data to cloud.
      // Only when the data was actually created while signed out (anon flag) — the local
      // store also mirrors server data, and re-uploading that on every launch resurrects
      // tasks deleted or completed on other devices.
      (async () => {
        if (!storage.hasAnonData()) return;
        const local = storage.loadAllLocal();
        const hasLocal = local.tasks.length > 0 || local.projects.length > 0;
        if (!hasLocal) return;
        try {
          for (const project of local.projects) {
            try { await api.createProject(project); } catch {}
          }
          for (const area of local.areas) {
            try { await api.createArea(area); } catch {}
          }
          for (const tag of local.tags) {
            try { await api.createTag(tag); } catch {}
          }
          for (const task of local.tasks) {
            try { await api.createTask(task); } catch {}
          }
          toast.success('Your local tasks have been synced to the cloud');
          storage.clearAnonDataFlag();
          qc.invalidateQueries();
        } catch {}
      })();
    }
    wasAuthRef.current = isAuthenticated;
  }, [isAuthenticated, qc]);

  return <>{children}</>;
}

const EMPTY_ARRAY = Object.freeze([]);

// Compat shim — preserves the existing useApp() API ({ state, dispatch, selectors })
// while reading data from React Query and UI state from Zustand.
export function useApp() {
  const qc = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const tasksQuery = useTasks();
  const projectsQuery = useProjects();
  const areasQuery = useAreas();
  const tagsQuery = useTags();

  const tasks = tasksQuery.data ?? EMPTY_ARRAY;
  const projects = projectsQuery.data ?? EMPTY_ARRAY;
  const areas = areasQuery.data ?? EMPTY_ARRAY;
  const tags = tagsQuery.data ?? EMPTY_ARRAY;

  const isLoading =
    isAuthenticated &&
    (tasksQuery.isLoading || projectsQuery.isLoading || areasQuery.isLoading || tagsQuery.isLoading);

  const error =
    tasksQuery.error?.message ||
    projectsQuery.error?.message ||
    areasQuery.error?.message ||
    tagsQuery.error?.message ||
    null;

  // Zustand UI state — read individually to keep selectors narrow
  const activeView = useAppStore((s) => s.activeView);
  const activeProjectId = useAppStore((s) => s.activeProjectId);
  const activeAreaId = useAppStore((s) => s.activeAreaId);
  const selectedTaskId = useAppStore((s) => s.selectedTaskId);
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);
  const commandPaletteOpen = useAppStore((s) => s.commandPaletteOpen);
  const focusModeOpen = useAppStore((s) => s.focusModeOpen);
  const brainDumpOpen = useAppStore((s) => s.brainDumpOpen);
  const searchQuery = useAppStore((s) => s.searchQuery);

  const setActiveView = useAppStore((s) => s.setActiveView);
  const setActiveProject = useAppStore((s) => s.setActiveProject);
  const setActiveArea = useAppStore((s) => s.setActiveArea);
  const setSelectedTask = useAppStore((s) => s.setSelectedTask);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const toggleCommandPalette = useAppStore((s) => s.toggleCommandPalette);
  const setCommandPalette = useAppStore((s) => s.setCommandPalette);
  const setFocusMode = useAppStore((s) => s.setFocusMode);
  const setBrainDump = useAppStore((s) => s.setBrainDump);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);

  const state = {
    activeView,
    activeProjectId,
    activeAreaId,
    selectedTaskId,
    sidebarCollapsed,
    commandPaletteOpen,
    focusModeOpen,
    brainDumpOpen,
    searchQuery,
    tasks,
    projects,
    areas,
    tags,
    isLoading,
    error,
  };

  // Helpers to mutate the React Query cache optimistically + persist to localStorage.
  const setTasks = useCallback(
    (updater) => {
      qc.setQueryData(['tasks'], (prev = []) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        storage.saveTasks(next);
        return next;
      });
    },
    [qc]
  );
  const setProjects = useCallback(
    (updater) => {
      qc.setQueryData(['projects'], (prev = []) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        storage.saveProjects(next);
        return next;
      });
    },
    [qc]
  );
  const setAreas = useCallback(
    (updater) => {
      qc.setQueryData(['areas'], (prev = []) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        storage.saveAreas(next);
        return next;
      });
    },
    [qc]
  );
  const setTagsCache = useCallback(
    (updater) => {
      qc.setQueryData(['tags'], (prev = []) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        storage.saveTags(next);
        return next;
      });
    },
    [qc]
  );

  // Fire-and-forget API call (only if authenticated). Toasts on failure via api.js.
  const fireApi = useCallback(
    (fn) => {
      if (!isAuthenticated) {
        // Signed-out mutation → local-only data now exists; sign-in migration should push it.
        storage.markAnonData();
        return;
      }
      Promise.resolve(fn()).catch((err) => {
        console.error('API mutation failed:', err);
      });
    },
    [isAuthenticated]
  );

  const dispatch = useCallback(
    (action) => {
      switch (action.type) {
        // ─── Navigation / UI ─────────────────────────────────
        case 'SET_ACTIVE_VIEW':
          setActiveView(action.payload);
          return;
        case 'SET_ACTIVE_PROJECT':
          setActiveProject(action.payload);
          return;
        case 'SET_ACTIVE_AREA':
          setActiveArea(action.payload);
          return;
        case 'SET_SELECTED_TASK':
          setSelectedTask(action.payload);
          return;
        case 'TOGGLE_SIDEBAR':
          toggleSidebar();
          return;
        case 'TOGGLE_COMMAND_PALETTE':
          toggleCommandPalette();
          return;
        case 'SET_COMMAND_PALETTE':
          setCommandPalette(action.payload);
          return;
        case 'OPEN_FOCUS_MODE':
          setFocusMode(true);
          return;
        case 'CLOSE_FOCUS_MODE':
          setFocusMode(false);
          return;
        case 'OPEN_BRAIN_DUMP':
          setBrainDump(true);
          return;
        case 'CLOSE_BRAIN_DUMP':
          setBrainDump(false);
          return;
        case 'SET_SEARCH_QUERY':
          setSearchQuery(action.payload);
          return;
        case 'SET_LOADING':
        case 'SET_ERROR':
        case 'SET_INITIAL_DATA':
          // Owned by React Query now — no-op for backward compat.
          return;

        // ─── Tasks ────────────────────────────────────────────
        case 'ADD_TASK': {
          const task = {
            id: generateId(),
            title: '',
            notes: '',
            status: 'inbox',
            dueDate: null,
            dueTime: null,
            reminder: null,
            projectId: null,
            areaId: null,
            tags: [],
            headingId: null,
            checklist: [],
            position: tasks.length,
            completedAt: null,
            createdAt: new Date().toISOString(),
            ...action.payload,
          };
          setTasks((prev) => [...prev, task]);
          setSelectedTask(task.id);
          fireApi(() => api.createTask(task));
          return;
        }
        case 'UPDATE_TASK': {
          setTasks((prev) =>
            prev.map((t) => (t.id === action.payload.id ? { ...t, ...action.payload } : t))
          );
          fireApi(() => api.updateTask(action.payload));
          return;
        }
        case 'COMPLETE_TASK': {
          setTasks((prev) =>
            prev.map((t) =>
              t.id === action.payload
                ? { ...t, status: 'logbook', completedAt: new Date().toISOString() }
                : t
            )
          );
          fireApi(() => api.completeTask(action.payload));
          return;
        }
        case 'UNCOMPLETE_TASK': {
          setTasks((prev) =>
            prev.map((t) =>
              t.id === action.payload ? { ...t, status: 'inbox', completedAt: null } : t
            )
          );
          fireApi(() => api.uncompleteTask(action.payload));
          return;
        }
        case 'DELETE_TASK': {
          setTasks((prev) => prev.filter((t) => t.id !== action.payload));
          if (selectedTaskId === action.payload) setSelectedTask(null);
          fireApi(() => api.deleteTask(action.payload));
          return;
        }
        case 'MOVE_TASK': {
          setTasks((prev) =>
            prev.map((t) =>
              t.id === action.payload.id
                ? { ...t, status: action.payload.status, projectId: action.payload.projectId || t.projectId }
                : t
            )
          );
          fireApi(() =>
            api.moveTask(action.payload.id, action.payload.status, action.payload.projectId)
          );
          return;
        }
        case 'REORDER_TASKS': {
          const next = action.payload;
          setTasks(() => next);
          fireApi(() =>
            api.reorderTasks(next.map((t) => ({ taskId: t.id, position: t.position })))
          );
          return;
        }
        case 'ADD_CHECKLIST_ITEM': {
          let updatedTask = null;
          setTasks((prev) =>
            prev.map((t) => {
              if (t.id !== action.payload.taskId) return t;
              updatedTask = {
                ...t,
                checklist: [
                  ...t.checklist,
                  { id: generateId(), title: action.payload.title, completed: false },
                ],
              };
              return updatedTask;
            })
          );
          if (updatedTask) fireApi(() => api.updateTask(updatedTask));
          return;
        }
        case 'TOGGLE_CHECKLIST_ITEM': {
          let updatedTask = null;
          setTasks((prev) =>
            prev.map((t) => {
              if (t.id !== action.payload.taskId) return t;
              updatedTask = {
                ...t,
                checklist: t.checklist.map((item) =>
                  item.id === action.payload.itemId
                    ? { ...item, completed: !item.completed }
                    : item
                ),
              };
              return updatedTask;
            })
          );
          if (updatedTask) fireApi(() => api.updateTask(updatedTask));
          return;
        }
        case 'DELETE_CHECKLIST_ITEM': {
          let updatedTask = null;
          setTasks((prev) =>
            prev.map((t) => {
              if (t.id !== action.payload.taskId) return t;
              updatedTask = {
                ...t,
                checklist: t.checklist.filter((item) => item.id !== action.payload.itemId),
              };
              return updatedTask;
            })
          );
          if (updatedTask) fireApi(() => api.updateTask(updatedTask));
          return;
        }

        // ─── Projects ─────────────────────────────────────────
        case 'ADD_PROJECT': {
          const project = {
            id: generateId(),
            name: action.payload.name || 'New Project',
            areaId: action.payload.areaId || null,
            status: 'active',
            headings: [],
            position: projects.length,
            deadline: null,
            ...action.payload,
          };
          setProjects((prev) => [...prev, project]);
          fireApi(() => api.createProject(project));
          return;
        }
        case 'UPDATE_PROJECT': {
          setProjects((prev) =>
            prev.map((p) => (p.id === action.payload.id ? { ...p, ...action.payload } : p))
          );
          fireApi(() => api.updateProject(action.payload));
          return;
        }
        case 'DELETE_PROJECT': {
          setProjects((prev) => prev.filter((p) => p.id !== action.payload));
          setTasks((prev) =>
            prev.map((t) => (t.projectId === action.payload ? { ...t, projectId: null } : t))
          );
          if (activeProjectId === action.payload) setActiveProject(null);
          fireApi(() => api.deleteProject(action.payload));
          return;
        }
        case 'ADD_HEADING': {
          let updatedProject = null;
          setProjects((prev) =>
            prev.map((p) => {
              if (p.id !== action.payload.projectId) return p;
              updatedProject = {
                ...p,
                headings: [
                  ...p.headings,
                  { id: generateId(), name: action.payload.name },
                ],
              };
              return updatedProject;
            })
          );
          if (updatedProject) fireApi(() => api.updateProject(updatedProject));
          return;
        }

        // ─── Areas ────────────────────────────────────────────
        case 'ADD_AREA': {
          const area = {
            id: generateId(),
            name: action.payload.name || 'New Area',
            position: areas.length,
            ...action.payload,
          };
          setAreas((prev) => [...prev, area]);
          fireApi(() => api.createArea(area));
          return;
        }
        case 'UPDATE_AREA': {
          setAreas((prev) =>
            prev.map((a) => (a.id === action.payload.id ? { ...a, ...action.payload } : a))
          );
          fireApi(() => api.updateArea(action.payload));
          return;
        }
        case 'DELETE_AREA': {
          setAreas((prev) => prev.filter((a) => a.id !== action.payload));
          setProjects((prev) =>
            prev.map((p) => (p.areaId === action.payload ? { ...p, areaId: null } : p))
          );
          fireApi(() => api.deleteArea(action.payload));
          return;
        }

        // ─── Tags ─────────────────────────────────────────────
        case 'ADD_TAG': {
          const tag = {
            id: generateId(),
            name: action.payload.name,
            color: action.payload.color || '#6B7280',
          };
          setTagsCache((prev) => [...prev, tag]);
          fireApi(() => api.createTag(tag));
          return;
        }
        case 'DELETE_TAG': {
          setTagsCache((prev) => prev.filter((t) => t.id !== action.payload));
          setTasks((prev) =>
            prev.map((t) => ({ ...t, tags: t.tags.filter((id) => id !== action.payload) }))
          );
          fireApi(() => api.deleteTag(action.payload));
          return;
        }

        default:
          return;
      }
    },
    [
      tasks.length,
      projects.length,
      areas.length,
      activeProjectId,
      selectedTaskId,
      setTasks,
      setProjects,
      setAreas,
      setTagsCache,
      setActiveView,
      setActiveProject,
      setActiveArea,
      setSelectedTask,
      toggleSidebar,
      toggleCommandPalette,
      setCommandPalette,
      setFocusMode,
      setBrainDump,
      setSearchQuery,
      fireApi,
    ]
  );

  // ─── Selectors ──────────────────────────────────────────
  const getTasksForView = useCallback(
    (view) => {
      switch (view) {
        case 'inbox':
          return tasks
            .filter((t) => t.status === 'inbox')
            .sort((a, b) => a.position - b.position);
        case 'today':
          return tasks
            .filter(
              (t) =>
                t.status === 'today' ||
                (t.dueDate &&
                  new Date(t.dueDate).toDateString() === new Date().toDateString() &&
                  t.status !== 'logbook')
            )
            .sort((a, b) => a.position - b.position);
        case 'upcoming':
          return tasks
            .filter((t) => t.dueDate && t.status !== 'logbook')
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
        case 'anytime':
          return tasks
            .filter((t) => t.status === 'anytime')
            .sort((a, b) => a.position - b.position);
        case 'someday':
          return tasks
            .filter((t) => t.status === 'someday')
            .sort((a, b) => a.position - b.position);
        case 'logbook':
          return tasks
            .filter((t) => t.status === 'logbook')
            .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
        default:
          return [];
      }
    },
    [tasks]
  );

  const getTasksForProject = useCallback(
    (projectId) =>
      tasks
        .filter((t) => t.projectId === projectId && t.status !== 'logbook')
        .sort((a, b) => a.position - b.position),
    [tasks]
  );

  const getProjectsForArea = useCallback(
    (areaId) => projects.filter((p) => p.areaId === areaId),
    [projects]
  );

  const getTaskCount = useCallback((view) => getTasksForView(view).length, [getTasksForView]);

  const searchTasks = useCallback(
    (query) => {
      const lower = query.toLowerCase();
      return tasks.filter(
        (t) => t.title.toLowerCase().includes(lower) || t.notes.toLowerCase().includes(lower)
      );
    },
    [tasks]
  );

  return {
    state,
    dispatch,
    getTasksForView,
    getTasksForProject,
    getProjectsForArea,
    getTaskCount,
    searchTasks,
  };
}
